import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Dimmer,
  Divider,
  Grid,
  Header,
  Loader,
} from 'semantic-ui-react';
import { required } from '@mhub/web-validations';
import { withTranslation } from 'react-i18next';

import Item from './Item';

import { flashErrorMessage, flashSuccessMessage } from '../../actions/flash';
import { serverRequestDelete } from '../../actions/fetchM';

import AppForm from '../../components/AppForm';
import CurrencySymbol from '../../components/CurrencySymbol';
import LoanAmount from '../../components/LoanAmount';

import { centsToFloat } from '../../utils/numbers/centsFloat';
import { resetDataChanges } from '../../utils/inputChanges';
import { validateForm } from '../../utils/validations';

import { namespaces, TRANSLATION_FILES } from '../../constants/translation';

class Delete extends Component {
  static propTypes = {
    checkChanges: PropTypes.func,
    data: PropTypes.oneOfType([
      PropTypes.object,
    ]),
    onUpdated: PropTypes.func,
    projectBankAccount: PropTypes.oneOfType([
      PropTypes.array,
    ]),
    projectCurrencies: PropTypes.oneOfType([
      PropTypes.array,
    ]),
    resetChanges: PropTypes.func,
    token: PropTypes.string,
  }
  static contextTypes = {
    router: PropTypes.object,
    store: PropTypes.object,
  }
  static getPropsToState = (props) => {
    const data = props.data;
    const projectBankAccount = props.projectBankAccount;
    const projectCurrencies = props.projectCurrencies;
    return {
      data,
      projectBankAccount,
      projectCurrencies,
    };
  }
  constructor(props) {
    super(props);
    const state = Delete.getPropsToState(props);
    this.validation = {
      cancelledReason: createRef(),
    };
    this.state = {
      genericErrMsg: props.t('An error occurred, please refresh page', { ns: TRANSLATION_FILES.translation }),
      loading: false,
      ...state,
    };
  }
  componentWillReceiveProps(nextProps) {
    const state = Delete.getPropsToState(nextProps);
    this.setState({ ...state });
  }
  async handleError(store, msg, errorObj, isBugsnag = false) {
    await this.setLoading(false);
    await store.dispatch(flashErrorMessage({ message: msg }));
    if (isBugsnag) Bugsnag.notifyException(errorObj);
  }
  setLoading(loading = false) {
    return new Promise((res) => {
      this.setState({ loading }, () => {
        res(loading);
      });
    });
  }
  renderCancelTransactionItemFooter() {
    const { data, projectCurrencies } = this.state;
    const { t } = this.props;
    return (
      <span>
        <AppForm
          onSubmit={this.handleConfirmCancelTransaction}
          ref={(ref) => { this.form = ref; }}
        >
          <Divider horizontal>
            <Header as="h4" style={{ marginBottom: '0', color: '#CE0000' }}>
            {t('Cancel Payment')}
          </Header>
          </Divider>
          <Header as="h5" style={{ marginTop: '0', color: '#CE0000' }}>
          {t('Cancel and deduct payment of')} <LoanAmount
            projectCurrencies={projectCurrencies}
            format="0,000,000.00"
            amount={centsToFloat(data.amountCents)}
            prefix={<CurrencySymbol />}
          /> ?
        </Header>
          <Grid verticalAlign="middle" style={{ marginTop: '-1rem' }}>
            <Grid.Column textAlign="left" width={2}>
            <b>{t('Reason')}</b>
          </Grid.Column>
            <Grid.Column textAlign="right" width={14}>
              <AppForm.Input
                type="text"
                hideMessage
                name="cancelledReason"
                validateLabel={t('Reason')}
                value={data.cancelledReason}
                onChange={this.handleInputChange}
                ref={this.validation.cancelledReason}
                validation={[required]}
                placeholder={t('Reason')}
              />
            </Grid.Column>
          </Grid>
          <Grid verticalAlign="middle" style={{ marginTop: '-1rem' }}>
            <Grid.Column textAlign="left" width={2} />
            <Grid.Column textAlign="left" width={7}>
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  this.handleResetInputChange('BookingFeePayment');
                }}
                fluid
                basic
                content={t('No', { ns: TRANSLATION_FILES.constants })}
              />
            </Grid.Column>
            <Grid.Column textAlign="left" width={7}>
              <Button
                fluid
                color="blue"
                onClick={this.handleConfirmCancelTransaction}
                content={t('Yes', { ns: TRANSLATION_FILES.constants })}
              />
            </Grid.Column>
          </Grid>
        </AppForm>
      </span>
    );
  }
  handleConfirmCancelTransaction = (event) => {
    event.preventDefault();
    const { store, router } = this.context;
    const { data, genericErrMsg } = this.state;
    const { t } = this.props;
    return AppForm.validate(this.validation, data)
      .then(async (validations) => {
        if (validateForm(store, validations)) return;
        try {
          await this.setLoading(true);
          const response = await serverRequestDelete(
            `/bookings/${router.params.id}/booking_fee_payments/${data.id}/`,
            store,
            this.props.token,
            { cancelledReason: data.cancelledReason },
          );
          await this.setLoading(false);
          this.handleResetInputChange();
          if (response.error) {
            this.handleError(store, response.message);
            return;
          }
          if (this.props.onUpdated) await this.props.onUpdated();
          store.dispatch(flashSuccessMessage({
            header: t('Cancel Success', { ns: TRANSLATION_FILES.translation }),
            message: t('Payment successfully cancelled', { ns: TRANSLATION_FILES.translation }),
          }));
        } catch (error) {
          this.handleResetInputChange();
          this.handleError(store, genericErrMsg, error, true);
        }
      });
  }
  handleInputChange = (event, { name, value }) => {
    if (this.props.checkChanges) this.props.checkChanges('isBookingFeePaymentChanged');
    const { data } = this.state;
    data[name] = value;
    this.setState({ data });
  }
  handleResetInputChange = (sectionName = 'BookingFeePayment') => {
    const { oriData } = this.state;
    const data = resetDataChanges(oriData, { cancelledReason: '' });
    this.setState({ data }, () => {
      if (this.props.resetChanges) this.props.resetChanges(sectionName);
    });
  }
  render() {
    const { data, projectBankAccount, projectCurrencies } = this.state;
    const { t } = this.props;
    return (
      <Grid centered>
        <Dimmer style={{ zIndex: 20 }} active={this.state.loading} inverted>
          {this.state.loading && <Loader inline="centered">{t('Loading', { ns: TRANSLATION_FILES.translation })}</Loader>}
        </Dimmer>
        <Item
          backgroundColor="#FFE5E5"
          data={data}
          footer={this.renderCancelTransactionItemFooter()}
          projectBankAccount={projectBankAccount}
          projectCurrencies={projectCurrencies}
          hideHeader
        />
      </Grid>
    );
  }
}
export default withTranslation(namespaces)(Delete);



// WEBPACK FOOTER //
// ./src/containers/BookingPayment/Delete.jsx